// src/lib/invitePersistence.ts
// Единая точка хранения «человек пришёл по ссылке-приглашению и ещё не дошёл до конца».
//
// Раньше эти три ключа писались и читались напрямую в четырёх файлах
// (InvitePage, WebLoginPage, AuthProvider), каждый со своим набором session/local и
// своим условием — из-за чего форма входа НЕ подхватывала уже сохранённый токен:
// он учитывался только в callback-режимах. Человек, попавший на /login не кнопкой со
// страницы приглашения (упавшая страница, вход с главной), отправлял email без токена и
// получал 403 `invite_required`, а письмо не уходило вовсе.

const TOKEN_KEY = 'pendingInviteToken'
const AUTO_ACCEPT_KEY = 'autoAcceptInvite'
const SAVED_AT_KEY = 'pendingInviteSavedAt'

/**
 * Сколько живёт сохранённый токен. Покрывает полный путь «открыл приглашение → запросил
 * письмо → перешёл по ссылке из письма» (сама ссылка живёт 15 минут) и при этом не даёт
 * чужому токену прилипнуть к следующему входу на общем компьютере.
 */
export const PENDING_INVITE_TTL_MS = 60 * 60 * 1000

export interface PendingInvite {
  token: string
  /** Приглашение надо применить сразу после возврата, без второго нажатия. */
  autoAccept: boolean
}

export interface PendingInviteSnapshot {
  sessionToken: string | null
  localToken: string | null
  autoAcceptToken: string | null
  savedAt: number | null
}

export interface SelectPendingInviteOptions {
  /** true — вернуть токен только если он помечен к автоприменению (редирект после входа). */
  requireAutoAccept?: boolean
  now?: number
  ttlMs?: number
}

/**
 * Чистое ядро выбора: без storage, чтобы правило проверялось тестом.
 *
 * Запись без `savedAt` сделана прошлой версией фронта и не принимается: невозможно
 * отличить незавершённый вход от токена, оставшегося на общем устройстве несколько
 * месяцев назад.
 */
export function selectPendingInvite(
  snapshot: PendingInviteSnapshot,
  options: SelectPendingInviteOptions = {}
): PendingInvite | null {
  const { requireAutoAccept = false, now = Date.now(), ttlMs = PENDING_INVITE_TTL_MS } = options

  const token = snapshot.sessionToken || snapshot.localToken
  if (!token) return null

  if (snapshot.savedAt === null) return null
  if (snapshot.savedAt > now || now - snapshot.savedAt > ttlMs) return null

  const autoAccept = snapshot.autoAcceptToken === token
  if (requireAutoAccept && !autoAccept) return null

  return { token, autoAccept }
}

function readStorage(storage: Storage | undefined, key: string): string | null {
  try {
    return storage?.getItem(key) ?? null
  } catch {
    return null
  }
}

function writeStorage(storage: Storage | undefined, key: string, value: string) {
  try {
    storage?.setItem(key, value)
  } catch {
    /* приватный режим браузера — вход не должен падать из-за недоступного storage */
  }
}

function dropStorage(storage: Storage | undefined, key: string) {
  try {
    storage?.removeItem(key)
  } catch {
    /* см. writeStorage */
  }
}

function getStorages(): { session: Storage | undefined; local: Storage | undefined } {
  if (typeof window === 'undefined') return { session: undefined, local: undefined }
  return { session: window.sessionStorage, local: window.localStorage }
}

export function snapshotPendingInvite(): PendingInviteSnapshot {
  const { session, local } = getStorages()
  const rawSavedAt = readStorage(local, SAVED_AT_KEY) ?? readStorage(session, SAVED_AT_KEY)
  const savedAt = rawSavedAt ? Number(rawSavedAt) : null

  return {
    sessionToken: readStorage(session, TOKEN_KEY),
    localToken: readStorage(local, TOKEN_KEY),
    autoAcceptToken: readStorage(session, AUTO_ACCEPT_KEY) ?? readStorage(local, AUTO_ACCEPT_KEY),
    savedAt: savedAt !== null && Number.isFinite(savedAt) ? savedAt : null
  }
}

export function readPendingInvite(options: SelectPendingInviteOptions = {}): PendingInvite | null {
  return selectPendingInvite(snapshotPendingInvite(), options)
}

/**
 * Токен разрешено передать в auth только после явного действия пользователя,
 * зафиксированного autoAccept-маркером.
 */
export function selectInviteTokenForAuth(
  snapshot: PendingInviteSnapshot,
  options: Pick<SelectPendingInviteOptions, 'now' | 'ttlMs'> = {}
): string | null {
  return selectPendingInvite(snapshot, { ...options, requireAutoAccept: true })?.token ?? null
}

export function buildInviteAwareLoginPath(inviteToken?: string | null): string {
  const normalized = typeof inviteToken === 'string' ? inviteToken.trim() : ''
  return normalized ? `/login?invite_token=${encodeURIComponent(normalized)}` : '/login'
}

export function savePendingInvite(token: string, options: { autoAccept?: boolean } = {}) {
  const { autoAccept = false } = options
  const { session, local } = getStorages()
  const savedAt = String(Date.now())

  for (const storage of [session, local]) {
    writeStorage(storage, TOKEN_KEY, token)
    writeStorage(storage, SAVED_AT_KEY, savedAt)
    if (autoAccept) {
      writeStorage(storage, AUTO_ACCEPT_KEY, token)
    } else {
      dropStorage(storage, AUTO_ACCEPT_KEY)
    }
  }
}

export function clearPendingInvite() {
  const { session, local } = getStorages()
  for (const storage of [session, local]) {
    dropStorage(storage, TOKEN_KEY)
    dropStorage(storage, AUTO_ACCEPT_KEY)
    dropStorage(storage, SAVED_AT_KEY)
  }
}
